import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ISettingsConfigComponent } from '../../models/util/settingsConfig';
import { AuthService } from '../../services/auth/auth.service';
import { MaterialModule } from '../../material/material.module';
import { PipesModule } from '../../pipes/pipes.module';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { PasswordUtils } from '../../utils/passwordUtils';
import { CommonModule } from '@angular/common';
import { isNullOrUndefined } from '@pineapplelab/util';
import { UpdatePasswordDto } from '../../models/dto/updatePasswordDTO';
import { environment } from '../../../environments/environment';
import { BrowserStorage } from '../../models/browserStorage';
import { ToasterTypeEnum } from '../../enums/toasterType.enum';
import { ToasterService } from '../../services/toaster/toaster.service';
import { HttpStatusCode } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ErrorLoginMessageEnum } from '../../enums/errorLoginMessage.enum';

@Component({
	selector: 'app-settings-password',
	standalone: true,
	imports: [
		MaterialModule,
		PipesModule,
		FormsModule,
		ReactiveFormsModule,
		CommonModule,
	],
	templateUrl: './settings-password.component.html',
	styleUrl: './settings-password.component.scss',
})
export class SettingsPasswordComponent implements OnInit, ISettingsConfigComponent, OnDestroy {
	private readonly authService = inject(AuthService);
	private readonly formBuilder = inject(FormBuilder);
	private readonly toasterService = inject(ToasterService);

	form: FormGroup;
	errors: { [key: string]: string } = {};
	MIN_AMOUNT_OF_CHARACTERS = PasswordUtils.PASSWORD_MIN_LENGTH;
	visibility: any = {
		currentPassword: false,
		newPassword: false,
		confirmNewPassword: false,
	};
	updatePasswordDto = new UpdatePasswordDto();
	submitting: boolean = false;
	subscription: Subscription;

	ngOnInit(): void {
		this.form = this.formBuilder.group(
			{
				currentPassword: ['', [Validators.required]],
				newPassword: [
					'',
					[Validators.required, PasswordUtils.passwordValidationFn],
				],
				confirmNewPassword: ['', [Validators.required]],
			},
			{ validators: this.confirmPasswordValidator() }
		);

		this.subscription = this.form.valueChanges.subscribe(() => {
			this.updateErrorMessages();
		});

		this.updateErrorMessages();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	updateErrorMessages(): void {
		this.updateCurrentPasswordErrors();
		this.updateNewPasswordErrors();
		this.updateConfirmNewPasswordErrors();
	}

	confirmPasswordValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const formGroup = control as FormGroup;
			this.checkSameAsCurrent(formGroup);
			this.checkPasswordMismatch(formGroup);
			return null;
		};
	}

	toggleVisibility(field: string): void {
		if (isNullOrUndefined(this.visibility?.[field])) {
			return;
		}

		this.visibility[field] = !this.visibility[field];
	}

	async change(): Promise<void> {
		this.submitting = true;
		this.updatePasswordDto.newPassword = this.form.value?.newPassword;
		this.updatePasswordDto.currentPassword =
			this.form.value?.currentPassword;

		try {
			const result = await this.authService.updatePasswordLogged(
				this.updatePasswordDto
			);
			await this.authService.afterLoginActions(result?.['accessToken']);
			this.toasterService.show(
				'login.reset_successfully',
				ToasterTypeEnum.SUCCESS
			);
		} catch (error) {
			this.processError(error);
		}

		this.submitting = false;
	}

	processError(error: Error | any): void {
		if (!error && !error?.error && !error.error?.message) {
			return;
		}
		if (error.status === HttpStatusCode.InternalServerError) {
			this.toasterService.showError('general.action_error');
			return;
		}
		this.processInvalidCurrentPassword(error);
		if (
			error.status === HttpStatusCode.BadRequest &&
			error.error.message.includes('different')
		) {
			this.toasterService.showError('account.new_password_different');
		}
	}

	processInvalidCurrentPassword(error: Error | any): void  {
		if (error.status === HttpStatusCode.Unauthorized && error.error.message.includes(ErrorLoginMessageEnum.INVALID_PASSWORD)) {
			this.toasterService.showError('general.error_current_password', {}, 'end');
			return;
		}
	}

	checkSameAsCurrent(formGroup: FormGroup) {
		const newPasswordControl = formGroup.get('newPassword');
		const newPassword = newPasswordControl?.value;
		const currentPassword = formGroup.get('currentPassword')?.value;

		if (currentPassword === newPassword) {
			newPasswordControl.setErrors({
				sameAsCurrent: 'account.new_password_different',
			});
		} else {
			if (
				newPasswordControl.errors &&
				newPasswordControl.errors?.['sameAsCurrent']
			) {
				delete newPasswordControl.errors?.['sameAsCurrent'];
				if (Object.keys(newPasswordControl.errors).length === 0) {
					newPasswordControl.setErrors(null);
				} else {
					newPasswordControl.updateValueAndValidity();
				}
			}
		}
	}

	checkPasswordMismatch(formGroup: FormGroup) {
		const newPassword = formGroup.get('newPassword')?.value;
		const confirmNewPasswordControl = formGroup.get('confirmNewPassword');
		const confirmNewPassword = confirmNewPasswordControl?.value;

		if (newPassword !== confirmNewPassword) {
			confirmNewPasswordControl.setErrors({ mismatch: true });
		} else {
			if (
				confirmNewPasswordControl.errors &&
				confirmNewPasswordControl.errors?.['mismatch']
			) {
				delete confirmNewPasswordControl.errors?.['mismatch'];
				if (
					Object.keys(confirmNewPasswordControl.errors).length === 0
				) {
					confirmNewPasswordControl.setErrors(null);
				} else {
					confirmNewPasswordControl.updateValueAndValidity();
				}
			}
		}
	}

	updateCurrentPasswordErrors() {
		const currentPasswordErrors = this.form.get('currentPassword').errors;
		this.errors['currentPassword'] = currentPasswordErrors?.['required']
			? 'account.current_password_required'
			: '';
	}

	updateNewPasswordErrors() {
		const newPasswordErrors = this.form.get('newPassword').errors;
		this.errors['newPassword'] = '';
		if (newPasswordErrors?.['required']) {
			this.errors['newPassword'] += 'account.new_password_required';
		}
		if (newPasswordErrors?.['pattern']) {
			this.errors['newPassword'] +=
				this.errors['newPassword'].length > 0 ? ' ' : '';
			this.errors['newPassword'] += this.form.get('newPassword')?.getError('pattern');
		}
		if (newPasswordErrors?.['sameAsCurrent']) {
			this.errors['newPassword'] +=
				this.errors['newPassword'].length > 0 ? ' ' : '';
			this.errors['newPassword'] +=
				'account.new_password_different';
		}
	}

	updateConfirmNewPasswordErrors() {
		const confirmNewPasswordErrors =
			this.form.get('confirmNewPassword').errors;
		this.errors['confirmNewPassword'] = '';
		if (confirmNewPasswordErrors?.['required']) {
			this.errors['confirmNewPassword'] +=
				'account.confirm_new_password_required';
		}
		if (confirmNewPasswordErrors?.['mismatch']) {
			this.errors['confirmNewPassword'] +=
				this.errors['confirmNewPassword'].length > 0 ? ' ' : '';
			this.errors['confirmNewPassword'] += 'account.passwords_must_match';
		}
	}

	hasDataChanged(): boolean {
		return false;
	}
}
